import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FlightProvider, useFlight } from './contexts/FlightContext';
import LoginScreen from './screens/LoginScreen';
import MainLayout from './components/MainLayout';
import FlightSelectionScreen from './screens/FlightSelectionScreen';
import { Tab } from './types';
import { CheckIcon, XCircleIcon, BellIcon, PaperAirplaneIcon, WifiSlashIcon, ClockIcon } from './components/Icons';
import InstallPwaPrompt from './components/InstallPwaPrompt';
import { database } from './services/firebase';
import { ref, onValue } from 'firebase/database';
import { updatePwaAssets } from './services/pwaService';

const App: React.FC = () => {
  return (
    <AuthProvider>
      <FlightProvider>
        <AppContent />
      </FlightProvider>
    </AuthProvider>
  );
};

const AppContent: React.FC = () => {
  const { user, appAssets } = useAuth();
  const { activeFlights, selectedFlightData, notification: localNotification, areNotificationsEnabled, logFinalizeTimestamp, selectedFlightSlotId, showNotification } = useFlight();
  const [showTour, setShowTour] = useState(false);
  const [isTourInitialized, setIsTourInitialized] = useState(false);
  const [isOnline, setIsOnline] = useState<boolean>(true);
  
  // State for global notifications from other users
  const [globalNotification, setGlobalNotification] = useState<{ station: string; triggeredBy: string } | null>(null);
  const globalNotificationTimer = useRef<number | null>(null);
  const lastGlobalNotificationTimestamp = useRef<number | null>(null);

  // State for handling deep links from PWA shortcuts
  const [initialTabOverride, setInitialTabOverride] = useState<Tab | null>(null);
  const [isFinalizeBannerDismissed, setIsFinalizeBannerDismissed] = useState(false);

  const roleDefaultTabs: { [key: string]: Tab } = {
    ADMIN: Tab.Admin,
    SUPERVISOR: Tab.CounterDistribution,
    AGENT: Tab.CrewMovement,
    VIEWER: Tab.FlightDashboard,
    LL: Tab.ArrivalDashboard,
  };
  
  const initialTab = initialTabOverride || (user && roleDefaultTabs[user.role]) || Tab.FlightDashboard;
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);

  const doorsClosedTime = useMemo(() => {
    if (!selectedFlightData) return null;
    const crewEntries = selectedFlightData.crewMovement['Doors Closed']?.crew;
    const pilotEntries = selectedFlightData.crewMovement['Doors Closed']?.pilots;
    const lastCrew = crewEntries?.[crewEntries.length - 1]?.time;
    const lastPilot = pilotEntries?.[pilotEntries.length - 1]?.time;
    if (lastCrew && lastPilot) {
      return new Date(lastCrew) > new Date(lastPilot) ? lastCrew : lastPilot;
    }
    return lastCrew || lastPilot || null;
  }, [selectedFlightData]);

  const notificationConfig = {
    success: {
      className: 'bg-gradient-to-r from-green-500 to-emerald-600 border border-green-400',
      icon: <CheckIcon className="w-6 h-6 mr-2"/>
    },
    error: {
      className: 'bg-gradient-to-r from-red-500 to-rose-600 border border-red-400',
      icon: <XCircleIcon className="w-6 h-6 mr-2"/>
    },
    info: {
      className: 'bg-gradient-to-r from-blue-500 to-sky-600 border border-blue-400',
      icon: <ClockIcon className="w-6 h-6 mr-2"/>
    }
  };

  // Global event listener for notifications dispatched from non-React modules
  useEffect(() => {
    const handleShowNotification = (event: Event) => {
        const customEvent = event as CustomEvent<{ message: string; type: 'success' | 'error' | 'info' }>;
        const { message, type } = customEvent.detail;
        showNotification(message, type || 'error');
    };

    window.addEventListener('show-notification', handleShowNotification);
    
    return () => {
        window.removeEventListener('show-notification', handleShowNotification);
    };
  }, [showNotification]);

  useEffect(() => {
    const connectedRef = ref(database, '.info/connected');
    const unsubscribe = onValue(connectedRef, (snapshot) => {
        setIsOnline(snapshot.val() === true);
    });
    return () => unsubscribe();
  }, []);

  // Effect to handle incoming remote notifications for visual toasts
  useEffect(() => {
    if (!selectedFlightData) return;
    const remoteNotification = selectedFlightData.lastNotification;
    if (!remoteNotification) return;

    // --- Visual Toast Logic for other users ---
    // Show a visual toast only if the notification is new and from another user
    if (remoteNotification.timestamp !== lastGlobalNotificationTimestamp.current) {
        lastGlobalNotificationTimestamp.current = remoteNotification.timestamp;
        
        if (remoteNotification.triggeredBy !== user?.username) {
            setGlobalNotification({
                station: remoteNotification.station,
                triggeredBy: remoteNotification.triggeredBy,
            });

            if (globalNotificationTimer.current) {
                clearTimeout(globalNotificationTimer.current);
            }
            globalNotificationTimer.current = window.setTimeout(() => {
                setGlobalNotification(null);
            }, 5000); // Show for 5 seconds
        }
    }
  }, [selectedFlightData, user?.username]);


  useEffect(() => {
    if (user && !isTourInitialized) {
      const hasCompletedTour = localStorage.getItem('hasCompletedTour');
      if (!hasCompletedTour) {
        setShowTour(true);
      }
      setIsTourInitialized(true);
    }
  }, [user, isTourInitialized]);
  
  // Effect to handle deep links from PWA shortcuts
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    
    // A simple mapping from URL-friendly param to the Tab enum value
    const tabMap: { [key: string]: Tab } = {
        'CrewMovement': Tab.CrewMovement,
        'CounterDistribution': Tab.CounterDistribution,
        'Security': Tab.Security,
    };

    if (tabParam && tabMap[tabParam]) {
        setInitialTabOverride(tabMap[tabParam]);
    }
  }, []);

  // Reset finalize banner dismiss state when flight changes
  useEffect(() => {
    setIsFinalizeBannerDismissed(false);
  }, [selectedFlightSlotId]);

  // Effect to update activeTab when user logs in or deep link changes
  useEffect(() => {
      setActiveTab(initialTab);
  }, [initialTab]);

  // Effect to dynamically update PWA icons and manifest
  useEffect(() => {
    // This effect runs whenever the app assets (e.g., logos, icons) change.
    // It calls a dedicated service to update the PWA manifest and icons
    // in the document's <head> tags, allowing for live branding updates.
    updatePwaAssets(appAssets);
  }, [appAssets]);

  const handleTourComplete = () => {
    localStorage.setItem('hasCompletedTour', 'true');
    setShowTour(false);
  };

  if (!user) {
    return <LoginScreen />;
  }
  
  if (!activeFlights || Object.keys(activeFlights).length === 0) {
    return <FlightSelectionScreen />;
  }

  const isFinalized = !!selectedFlightData?.finalizeTimestamp;
  const showFinalizeBanner = doorsClosedTime && !isFinalized && !isFinalizeBannerDismissed && activeTab !== Tab.FlightDashboard;

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        className={`fixed top-0 left-0 right-0 bg-amber-600 text-white text-center text-sm py-1.5 z-[60] shadow-lg transition-transform duration-300 ease-in-out ${isOnline ? '-translate-y-full' : 'translate-y-0'}`}
      >
        <div className="container mx-auto flex items-center justify-center gap-2">
          <WifiSlashIcon className="w-5 h-5"/>
          <span>You are offline. Changes will sync when reconnected.</span>
        </div>
      </div>
      <MainLayout activeTab={activeTab} setActiveTab={setActiveTab} showTour={showTour} onTourComplete={handleTourComplete} />
      
      {/* --- NOTIFICATIONS AREA --- */}
      {/* Finalize Flight Banner */}
      {showFinalizeBanner && doorsClosedTime && (
        <div role="alert" className="fixed top-5 left-1/2 -translate-x-1/2 z-50 p-4 rounded-lg shadow-lg text-white bg-gradient-to-r from-amber-500 to-orange-600 border border-amber-400 w-full max-w-lg">
            <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <PaperAirplaneIcon className="w-6 h-6 mr-3 animate-pulse"/>
                    <div>
                        <p className="font-bold">Doors Closed at {new Date(doorsClosedTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>
                        <p className="text-sm text-amber-200">Please finalize the flight.</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button 
                        onClick={logFinalizeTimestamp}
                        className="bg-white/20 hover:bg-white/30 text-white font-bold py-1 px-3 rounded text-sm"
                    >
                        Finalize
                    </button>
                    <button 
                        onClick={() => setIsFinalizeBannerDismissed(true)}
                        className="bg-white/20 hover:bg-white/30 text-white font-bold py-1 px-3 rounded text-sm"
                    >
                        OK
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* Local notifications (for user's own actions or global errors) */}
      {localNotification && (
        <div role="alert" className={`toast-notification fixed top-5 right-5 z-50 p-4 rounded-lg shadow-lg text-white ${notificationConfig[localNotification.type].className}`}>
          <div className="flex items-center">
            {notificationConfig[localNotification.type].icon}
            {localNotification.message}
          </div>
        </div>
      )}

      {/* Global notifications (from other users) */}
      {globalNotification && areNotificationsEnabled && (
        <div role="alert" className="toast-notification fixed top-24 left-1/2 -translate-x-1/2 z-50 p-4 rounded-lg shadow-lg text-white bg-gradient-to-r from-sky-500 to-cyan-500 border border-sky-400">
           <div className="flex items-center">
            <BellIcon className="w-6 h-6 mr-3 animate-pulse" />
            <div>
                <p className="font-bold">{globalNotification.station} recorded</p>
                <p className="text-sm text-sky-200">by {globalNotification.triggeredBy}</p>
            </div>
           </div>
        </div>
      )}
      <InstallPwaPrompt />
    </>
  );
};

export default App;